// ---------------------------------------------------------
// Datei: PhotoAnnotationEditor.tsx
// Zweck: Freihand-Markierung (mehrere Farben) + Beschriftung + Zoom/Verschieben auf einem Foto,
//        für die Projektvorbereitung-Phase (Ticket #928, Anhang "Foto-Beschriftung/-Markierung").
// Kontext: Nutzt Konva.js (react-konva) — zweiter Bibliothekswechsel dieser Ausbaustufe, nach
//          signature_pad (kein Zoom/Pan) und Fabric.js (wiederholte, nicht auflösbare
//          Größen-/Zentrierungs-Bugs zwischen Fabrics internem "Backstore" und der per CSS
//          gesetzten Bildschirm-Darstellungsgröße — trotz mehrfach über Quellcode UND einen
//          echten Playwright-Testlauf verifizierter korrekter Fabric-Konfiguration blieb der
//          Fehler beim Nutzer reproduzierbar). Konva braucht diese Backstore/CSS-Trennung nicht:
//          die Stage-Größe wird direkt in Pixeln gesetzt (per ResizeObserver an die tatsächliche
//          Containerbreite gekoppelt, kein einmaliger/verspäteter clientWidth-Messwert), Zoom/Pan
//          laufen über Stage-`scale`/`position` (Pan nativ über die `draggable`-Prop, kein
//          manuelles relativePan-Wiring mehr nötig), und der hochauflösende Export nutzt Konvas
//          eingebauten `pixelRatio`-Parameter in `toDataURL()`.
//          Foto liegt als Konva-`Image`-Node im Hintergrund, Striche sind `Line`-Nodes in
//          React-State (`lines`) — "Rückgängig" entfernt einfach den letzten Array-Eintrag.
// Stand: Reine Client-Komponente, kein Server-Zugriff — Aufrufer entscheidet, ob das Ergebnis
//        neu hochgeladen oder eine bestehende Datei ersetzt wird.
// ---------------------------------------------------------

import React, { useEffect, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Line } from 'react-konva';
import type Konva from 'konva';

const BRUSH_WIDTH = 4;
// Export-Auflösung (längste Kante) — begrenzt nur außergewöhnlich große Fotos, kein Einfluss auf
// die Bildschirm-Darstellungsgröße.
const MAX_DIMENSION = 1600;
const FALLBACK_DISPLAY_WIDTH = 480;

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

const PEN_COLORS: Array<{ value: string; label: string }> = [
  { value: '#dc2626', label: 'Rot' },
  { value: '#16a34a', label: 'Grün' },
  { value: '#2563eb', label: 'Blau' },
  { value: '#f59e0b', label: 'Gelb' },
  { value: '#111827', label: 'Schwarz' },
];

interface StrokeLine {
  points: number[];
  color: string;
}

// Lädt ein Bild als natives HTMLImageElement (liefert naturalWidth/naturalHeight für Konvas
// Image-Node) — dasselbe Muster wie das `use-image`-Paket, hier bewusst ohne Zusatz-Abhängigkeit
// inline gehalten, da nur dieser eine Anwendungsfall gebraucht wird.
const useHtmlImage = (src: string | undefined | null) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!src) {
      setImage(null);
      return;
    }
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) {
        setImage(img);
      }
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return image;
};

interface PhotoAnnotationEditorProps {
  // Zunächst angezeigtes Bild — bei einem bereits markierten Foto die markierte Version, sonst
  // das Original.
  imageDataUrl: string;
  // Nur gesetzt, wenn es eine vom Original abweichende markierte Version gibt: das unangetastete
  // Original, zu dem "Löschen" zurückspringt (siehe handleClear). Ist original === Anzeige (kein
  // markiertes Foto vorhanden), muss dieses Prop weggelassen werden — "Löschen" löscht dann wie
  // gewohnt nur die in dieser Sitzung gezeichneten Striche auf demselben Bild.
  originalImageDataUrl?: string;
  // Google Drive liefert eine leere description als JSON-`null`, nicht als fehlenden Key.
  initialCaption?: string | null;
  onSave: (annotatedDataUrl: string, caption: string) => void;
  // Nur relevant, wenn originalImageDataUrl gesetzt ist (es also eine markierte Version gibt):
  // wurde per "Löschen" auf das Original zurückgesprungen und seither kein neuer Strich
  // gezeichnet, ist die markierte Version nur noch ein unverändertes Duplikat des Originals —
  // "Übernehmen" ruft dann onDelete() statt onSave() auf, damit sie entfernt statt erneut
  // (unverändert) gespeichert wird.
  onDelete?: () => void;
  onCancel: () => void;
  saving?: boolean;
}

const PhotoAnnotationEditor: React.FC<PhotoAnnotationEditorProps> = ({
  imageDataUrl,
  originalImageDataUrl,
  initialCaption = '',
  onSave,
  onDelete,
  onCancel,
  saving = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const isDrawingRef = useRef(false);

  const displayImage = useHtmlImage(imageDataUrl);
  const hasSeparateOriginal = Boolean(originalImageDataUrl) && originalImageDataUrl !== imageDataUrl;
  const originalImage = useHtmlImage(hasSeparateOriginal ? originalImageDataUrl : null);

  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [lines, setLines] = useState<StrokeLine[]>([]);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [caption, setCaption] = useState(initialCaption ?? '');
  const [penColor, setPenColor] = useState(PEN_COLORS[0].value);
  // Verschieben ist der Standardmodus (Nutzeranfrage) — eine Farbe auszuwählen wechselt bewusst
  // automatisch in den Zeichnen-Modus (siehe Farbpalette unten), ein separater "Zeichnen"-Button
  // ist dadurch nicht mehr nötig.
  const [mode, setMode] = useState<'draw' | 'pan'>('pan');
  const [zoomLevel, setZoomLevel] = useState(MIN_ZOOM);

  // Misst die tatsächliche Containerbreite per ResizeObserver — reagiert zuverlässig auf jede
  // Layout-Änderung (z. B. Modal-Öffnungsanimation), im Gegensatz zu einem einmaligen
  // clientWidth-Messwert direkt nach dem Mounten, der wiederholt zu falschen/verspäteten Werten
  // geführt hatte.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !displayImage) {
      return;
    }

    const naturalWidth = displayImage.naturalWidth || 1;
    const naturalHeight = displayImage.naturalHeight || 1;

    const update = () => {
      const width = Math.max(1, el.clientWidth || FALLBACK_DISPLAY_WIDTH);
      const height = Math.round(width * (naturalHeight / naturalWidth));
      setDisplaySize({ width, height });
    };

    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [displayImage]);

  useEffect(() => {
    setLines([]);
    setShowingOriginal(false);
    setZoomLevel(MIN_ZOOM);
    setMode('pan');
    // Bei Bildwechsel auch eine evtl. noch bestehende Verschiebung vom vorherigen Foto
    // zurücksetzen — die Stage-Instanz bleibt über einen Bildwechsel hinweg bestehen (kein echtes
    // Unmount/Remount), Position liegt bewusst nicht als React-Prop vor (siehe stageScale-
    // Kommentar unten), muss also hier separat zurückgesetzt werden.
    stageRef.current?.position({ x: 0, y: 0 });
  }, [imageDataUrl]);

  const naturalWidth = displayImage?.naturalWidth || 0;
  const naturalHeight = displayImage?.naturalHeight || 0;
  const ready = Boolean(displayImage) && displaySize.width > 0;
  const fitScale = naturalWidth > 0 ? displaySize.width / naturalWidth : 1;
  const activeImage = showingOriginal && originalImage ? originalImage : displayImage;
  // Bewusst als React-PROP (nicht imperativ über die Ref) gesetzt: react-konva rendert über einen
  // eigenen React-Reconciler, dessen Ref-Zuweisung beim allerersten Mount nicht garantiert vor dem
  // ersten passiven Effekt der Elternkomponente abgeschlossen ist — ein rein imperativer
  // `stage.scale(...)`-Aufruf in einem `useEffect` konnte dadurch beim ersten Öffnen ins Leere
  // laufen (Foto blieb bei Konvas Default-Skalierung 1, wurde erst durch manuelles Zoomen
  // korrigiert). Als deklarative Prop gibt es dagegen keine Race Condition — Konva übernimmt den
  // Wert garantiert bei jedem Commit. Nur `position` (Verschieben) bleibt imperativ, da sie mit
  // Konvas eigenem `draggable`-Drag-Handling in Konflikt geraten würde.
  const stageScale = ready ? fitScale * zoomLevel : 1;

  const zoomToLevel = (nextZoomLevel: number) => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const oldScale = stage.scaleX() || fitScale;
    const newScale = fitScale * nextZoomLevel;
    const center = { x: displaySize.width / 2, y: displaySize.height / 2 };
    const focalPoint = {
      x: (center.x - stage.x()) / oldScale,
      y: (center.y - stage.y()) / oldScale,
    };
    stage.position({
      x: center.x - focalPoint.x * newScale,
      y: center.y - focalPoint.y * newScale,
    });
    setZoomLevel(nextZoomLevel);
  };

  const handleZoomIn = () => {
    zoomToLevel(Math.min(MAX_ZOOM, +(zoomLevel + ZOOM_STEP).toFixed(2)));
  };

  const handleZoomOut = () => {
    const next = Math.max(MIN_ZOOM, +(zoomLevel - ZOOM_STEP).toFixed(2));
    if (next <= MIN_ZOOM) {
      stageRef.current?.position({ x: 0, y: 0 });
      setZoomLevel(MIN_ZOOM);
    } else {
      zoomToLevel(next);
    }
  };

  // Begrenzt einen Punkt auf die Bildgrenzen — ohne das könnte man beim Zeichnen nahe am Rand
  // (v. a. gezoomt) auf den grauen Bereich außerhalb des Fotos hinausgeraten (Nutzeranfrage).
  const clampToImage = (point: { x: number; y: number }) => ({
    x: Math.min(Math.max(point.x, 0), naturalWidth || 0),
    y: Math.min(Math.max(point.y, 0), naturalHeight || 0),
  });

  const handlePointerDown = () => {
    if (mode !== 'draw' || !ready) {
      return;
    }
    const stage = stageRef.current;
    const pos = stage?.getRelativePointerPosition();
    if (!pos) {
      return;
    }
    const clamped = clampToImage(pos);
    isDrawingRef.current = true;
    setLines((prev) => [...prev, { points: [clamped.x, clamped.y], color: penColor }]);
  };

  const handlePointerMove = () => {
    if (!isDrawingRef.current) {
      return;
    }
    const stage = stageRef.current;
    const pos = stage?.getRelativePointerPosition();
    if (!pos) {
      return;
    }
    const clamped = clampToImage(pos);
    setLines((prev) => {
      const last = prev[prev.length - 1];
      if (!last) {
        return prev;
      }
      const updated: StrokeLine = { ...last, points: [...last.points, clamped.x, clamped.y] };
      return [...prev.slice(0, -1), updated];
    });
  };

  const handlePointerUp = () => {
    isDrawingRef.current = false;
  };

  const handleUndo = () => {
    setLines((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    if (!showingOriginal && originalImage) {
      // Erster Klick auf "Löschen" bei einem markierten Foto springt zurück auf das unangetastete
      // Original (Nutzeranfrage) — jeder weitere Klick löscht danach nur noch die neu gezeichneten
      // Striche auf diesem Original, genau wie bei einem Foto ohne markierte Version.
      setShowingOriginal(true);
    }
    setLines([]);
  };

  const handleSave = () => {
    const stage = stageRef.current;
    if (!stage || !naturalWidth) {
      return;
    }

    // Zurück aufs Original gesprungen (Löschen) und seither nichts Neues gezeichnet — die
    // markierte Version wäre dann nur ein unverändertes Duplikat des Originals. Statt sie so
    // erneut zu speichern, wird sie entfernt (Nutzeranfrage).
    if (showingOriginal && onDelete && lines.length === 0) {
      onDelete();
      return;
    }

    // Nutzer-Rückmeldung: weder neu gezeichnet noch die Beschriftung geändert — dann nichts
    // speichern, sonst entstünde eine neue "-markiert"-Datei (bzw. ein überflüssiger Re-Upload
    // einer unveränderten bestehenden Markierung), obwohl inhaltlich gar nichts markiert oder
    // beschriftet wurde.
    const captionChanged = caption.trim() !== (initialCaption ?? '').trim();
    if (lines.length === 0 && !captionChanged) {
      onCancel();
      return;
    }

    // Export muss das VOLLE Foto liefern, unabhängig vom aktuellen Zoom-/Verschiebungs-Zustand —
    // toDataURL() rendert sonst nur den gerade sichtbaren (ggf. gezoomten/verschobenen)
    // Ausschnitt. Transformation dafür kurz auf "volles Bild sichtbar" zurücksetzen, danach
    // wiederherstellen.
    const previousScale = { x: stage.scaleX(), y: stage.scaleY() };
    const previousPosition = { x: stage.x(), y: stage.y() };
    stage.scale({ x: fitScale, y: fitScale });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();

    // MAX_DIMENSION begrenzt die LÄNGSTE Kante, nicht pauschal die Breite — bei einem
    // Hochformat-Foto (naturalHeight > naturalWidth) würde ein reiner Breiten-Cap die tatsächlich
    // längste Kante (die Höhe) über MAX_DIMENSION hinaus exportieren (Autoreview-Finding).
    const longestNaturalEdge = Math.max(naturalWidth, naturalHeight);
    const exportScale = Math.min(1, MAX_DIMENSION / longestNaturalEdge);
    const pixelRatio = (naturalWidth * exportScale) / displaySize.width;
    const dataUrl = stage.toDataURL({ mimeType: 'image/jpeg', quality: 0.9, pixelRatio });

    stage.scale(previousScale);
    stage.position(previousPosition);
    stage.batchDraw();

    onSave(dataUrl, caption.trim());
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
      <div
        ref={containerRef}
        className="w-full overflow-hidden rounded border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900"
      >
        {ready && (
          <Stage
            ref={stageRef}
            width={displaySize.width}
            height={displaySize.height}
            scaleX={stageScale}
            scaleY={stageScale}
            // Bei zoomLevel === MIN_ZOOM deckt das Foto den Viewport exakt ab — ein Ziehen hätte
            // nichts zum Verschieben und könnte das Bild nur ohne Möglichkeit zum Zurücksetzen aus
            // dem sichtbaren Bereich schieben (der "−"-Button ist dann deaktiviert). Draggable
            // deshalb erst ab tatsächlichem Zoom aktiv, unabhängig vom (als Zeichnen-Gegenstück
            // immer wählbaren) Verschieben-Modus.
            draggable={mode === 'pan' && zoomLevel > MIN_ZOOM}
            className="touch-none"
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          >
            <Layer>
              {activeImage && (
                <KonvaImage image={activeImage} x={0} y={0} width={naturalWidth} height={naturalHeight} />
              )}
              {lines.map((line, index) => (
                <Line
                  key={index}
                  points={line.points}
                  stroke={line.color}
                  strokeWidth={fitScale > 0 ? BRUSH_WIDTH / fitScale : BRUSH_WIDTH}
                  lineCap="round"
                  lineJoin="round"
                  tension={0}
                  listening={false}
                />
              ))}
            </Layer>
          </Stage>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setMode('pan')}
          disabled={!ready}
          aria-label="Verschieben"
          aria-pressed={mode === 'pan'}
          className={`w-8 h-8 flex items-center justify-center rounded-full border-2 disabled:opacity-50 ${
            mode === 'pan'
              ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
          }`}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="5 9 2 12 5 15" />
            <polyline points="9 5 12 2 15 5" />
            <polyline points="15 19 12 22 9 19" />
            <polyline points="19 9 22 12 19 15" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <line x1="12" y1="2" x2="12" y2="22" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          {PEN_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              aria-label={`Stiftfarbe ${color.label}`}
              onClick={() => {
                setPenColor(color.value);
                setMode('draw');
              }}
              className={`w-8 h-8 rounded-full border-2 ${
                penColor === color.value && mode === 'draw' ? 'border-gray-900 dark:border-white' : 'border-transparent'
              }`}
              style={{ backgroundColor: color.value }}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleUndo}
            disabled={!ready}
            aria-label="Rückgängig"
            className="w-9 h-9 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 14 4 9 9 4" />
              <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={!ready}
            aria-label="Löschen"
            className="w-9 h-9 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={!ready || zoomLevel <= MIN_ZOOM}
            aria-label="Verkleinern"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
          >
            −
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[3.5rem] text-center">
            {Math.round(zoomLevel * 100)}&nbsp;%
          </span>
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={!ready || zoomLevel >= MAX_ZOOM}
            aria-label="Vergrößern"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
          >
            +
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Beschriftung</label>
        <textarea
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          rows={2}
          className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-2 text-sm"
          placeholder="z. B. Position Außeneinheit"
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!ready || saving}
          className="px-4 py-2 text-sm rounded bg-primary-600 text-white disabled:opacity-50"
        >
          {saving ? 'Wird gespeichert…' : 'Übernehmen'}
        </button>
      </div>
    </div>
  );
};

export default PhotoAnnotationEditor;
